import { CLM } from '@/common/schema/claims';
import { CLP } from './schema/claims-process';
import assert from 'assert';
import { unexpectedCodePath } from '@/fx/data-helpers';

type UrlFactory<T> = (params: T) => string;
export type UrlFactoryMap = {
  [key: string]: UrlFactory<any> | UrlFactoryMap;
};

const hostProtocol = process.env.NEXT_PUBLIC_HOST_PROTOCOL;

export function urls({ ts, pathname }: {
  ts?:       string;
  pathname?: string;
}) {
  const t0 = ts === "NOW" || ts === undefined ? Date.now().toString()
           : (ts === "NONE" ? undefined : ts);

  const params = [
    ...(t0 === undefined ? [] : [`ts=${t0}`]),
  ].join('&');

  function paramsWith({ ts, cbm, refresh, redirect, ...rest }: { ts?: string, cbm?: string, refresh?: boolean, redirect?: string, [key: string]: any }) {
    const t1 = ts === "NOW" || ts === undefined ? Date.now().toString()
             : (ts === "NONE" ? undefined : ts ?? t0);

    return [
      ...(t1                   === undefined ? [] : [`ts=${t1}`            ]),
      ...(cbm                  === undefined ? [] : [`cbm=${cbm}`          ]),
      ...(redirect             === undefined ? [] : [`redirect=${redirect}`]),

      ...(refresh === true ? [`refresh=true`] : []),

      ...Object.entries(rest).flatMap(([k, v]) => v === undefined ? [] : [`${k}=${v}`])
    ].join('&');
  }
  function mergedParams({ existingParams, additionalParams }: {
    existingParams:   URLSearchParams,
    additionalParams: URLSearchParams,
  }) {
    return new URLSearchParams({
      ...Object.fromEntries(existingParams),
      ...Object.fromEntries(additionalParams)
    });
  }
  function pathWithMergedParams({ pathWithParams, additionalParams: additionalParams_ }: {
    pathWithParams:   string,
    additionalParams: string,
  }): string {
    const [basePath, existingParams_] = pathWithParams.split('?');
    const mergedParams_ = mergedParams({
      existingParams:   new URLSearchParams(existingParams_  ),
      additionalParams: new URLSearchParams(additionalParams_)
    });
    return `${basePath}?${mergedParams_.toString()}`;
  }

  const normalizedPath = (p: string) => {
    const withoutBorderSlashes = p.replace(/^\/+|\/+$/g, '');
    return withoutBorderSlashes;
  }

  const url = ({ app, path: path_, params: params_ }: { app: CLM.App, path: string, params?: { ts?: string, cbm?: string, refresh?: boolean, [key: string]: any } }) => {
    assert(app !== undefined, "App is required for URL");

    const protocol = hostProtocol;
    const hostName = app.hostname;
    const host     = `${protocol}://${hostName}`;

    const appBasePath = app.pathname;
    const path = [
      normalizedPath(appBasePath),
      normalizedPath(path_)
    ].filter(p => p !== '').join('/');

    const params = paramsWith(params_ ?? { });
    const hasParams = params.length > 0;

    return hasParams ? `${host}/${path}?${params}`
                     : `${host}/${path}`;
  }

  const coreUrls = ({
    current: ({ ts, cbm, refresh }: { ts?: string, cbm?: string, refresh?: boolean }) => {
      assert(pathname !== undefined, "pathname is required for current URL");
      return pathWithMergedParams({
        pathWithParams:   pathname,
        additionalParams: paramsWith({ ts, cbm, refresh })
      });
    },

    sharableClaimLink: ({ claimId }: { claimId?: CLM.ClaimId } = {}) => url({ app: CLM.App.CLAIMS_DIS, path: `/claims/${claimId?.asStr}` }),

    home: ({ ts, app }: { ts?: string, app: CLM.App }) => url({ app, path: `/claims`, params: { ts: "NONE" } }),

    root:    ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.ROOT, path: `/`, params: { ts } }),
    login:   ({ ts }: { ts?: string } = {}) => `/login?${  paramsWith({ ts })}`,
    logout:  ({ ts }: { ts?: string } = {}) => `/logout?${ paramsWith({ ts })}`,
    sitemap: ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.SITE_MAP, path: `/`, params: { ts } }),
    healthCheck: {
      default:  ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.HEALTH_CHECK, path: `/`,          params: { ts } }),
      file:     ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.HEALTH_CHECK, path: `/file`,     params: { ts } }),
      user:     ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.HEALTH_CHECK, path: `/user`,     params: { ts } }),
      commands: ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.HEALTH_CHECK, path: `/commands`, params: { ts } }),
      csv:      ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.HEALTH_CHECK, path: `/csv`, params: { ts } }),
      cookies:  ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.HEALTH_CHECK, path: `/cookies`, params: { ts } }),
    },
    developerTools: {
      default: ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.DEVELOPER_TOOLS, path: `/`, params: { ts } }),
      claims:  ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.DEVELOPER_TOOLS, path: `/claims`, params: { ts } }),
      claim: {
        files: ({ ts, claimId, cbm }: { ts?: string, claimId: CLM.ClaimId , cbm?: string }) => url({ app: CLM.App.DEVELOPER_TOOLS, path: `/claims/${claimId.asStr}/files`, params: { ts, cbm } })
      },
      backfill:  ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.DEVELOPER_TOOLS, path: `/backfill`, params: { ts } }),
      resources: ({ ts, resourceType, resourceCount }: { ts?: string, resourceType?: string, resourceCount?: number } = {}) => url({ app: CLM.App.DEVELOPER_TOOLS, path: `/resources/new`, params: { ts, t: resourceType, c: resourceCount } }),
    },
    hud: {
      clear:               ({ ts }: { ts?: string }) => url({ app: CLM.App.HUD, path: `/clear`, params: { ts } }),
      reloadOldTestClaims: ({ ts }: { ts?: string }) => url({ app: CLM.App.HUD, path: `/reload-old-test-claims`, params: { ts } }),

      reloadTestClaims: ({ ts, collection = '', redirect }: { ts?: string, collection?: string, redirect?: string }) => url({ app: CLM.App.HUD, path: `/reload-test-claims/${collection}`, params: { ts, redirect } }),

      stages: {
        default: ({ ts       }: { ts?: string                       } = {}) => url({ app: CLM.App.HUD, path: `/stages`, params: { ts } }),
        select:  ({ ts, v, s }: { ts?: string, v: string, s: number }     ) => url({ app: CLM.App.HUD, path: `/stages/select/${v}-${s}`, params: { ts } }),
        clear:   ({ ts       }: { ts?: string                       } = {}) => url({ app: CLM.App.HUD, path: `/stages/clear`, params: { ts } }),
      },
      user: {
        impersonate: ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.HUD, path: `/user/impersonate`, params: { ts } }),
        new: {
          delete: ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.HUD, path: `/user/new/delete`, params: { ts } }),
        }
      },
      products: ({ ts, i }: { ts?: string, i?: string } = {}) => url({ app: CLM.App.HUD, path: `/products`, params: { ts, i } }),
    },
    claimsApp: {
      approvers: {
        noAccess: ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.CLAIMS_BBC_APR, path: `/no-access`, params: { ts } }),

        home:     ({ ts }: { ts?: string } = {}) => coreUrls.claimsApp.approvers.claims({ ts }),
        claims:   ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.CLAIMS_BBC_APR, path: `/claims`, params: { ts: "NONE" } }),
        claim: {
          summary:     ({ ts, claimId, cbm }: { ts?: string, claimId: CLM.ClaimId, cbm?: string }) => url({ app: CLM.App.CLAIMS_BBC_APR, path: `/claims/${claimId.asStr}`,             params: { ts, cbm } }),
          approvals:   ({ ts, claimId      }: { ts?: string, claimId: CLM.ClaimId               }) => url({ app: CLM.App.CLAIMS_BBC_APR, path: `/claims/${claimId.asStr}/approvals`,   params: { ts      } }),
          products:    ({ ts, claimId      }: { ts?: string, claimId: CLM.ClaimId               }) => url({ app: CLM.App.CLAIMS_BBC_APR, path: `/claims/${claimId.asStr}/products`,    params: { ts      } }),
          destruction: ({ ts, claimId      }: { ts?: string, claimId: CLM.ClaimId               }) => url({ app: CLM.App.CLAIMS_BBC_APR, path: `/claims/${claimId.asStr}/destruction`, params: { ts      } }),
          credit:      ({ ts, claimId      }: { ts?: string, claimId: CLM.ClaimId               }) => url({ app: CLM.App.CLAIMS_BBC_APR, path: `/claims/${claimId.asStr}/credit`,      params: { ts      } }),
          files:       ({ ts, claimId      }: { ts?: string, claimId: CLM.ClaimId               }) => url({ app: CLM.App.CLAIMS_BBC_APR, path: `/claims/${claimId.asStr}/files`,       params: { ts      } }),
        }
      },
      distributors: {
        noAccess: ({ ts, claimId }: { ts?: string, claimId?: CLM.ClaimId }) => url({ app: CLM.App.CLAIMS_DIS, path: `/no-access${claimId ? `/${claimId.asStr}` : ''}`, params: { ts } }),
        home:     ({ ts }: { ts?: string } = {}) => coreUrls.claimsApp.distributors.claims({ ts }),
        claims:   ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.CLAIMS_DIS, path: `/claims`,     params: { ts: "NONE" } }),
        newClaim: ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.CLAIMS_DIS, path: `/claims/new`, params: { ts } }),
        claim: {
          submitted:   ({ ts, claimId, cbm }: { ts?: string, claimId: CLM.ClaimId, cbm?: string }) => url({ app: CLM.App.CLAIMS_DIS, path: `/claims/${claimId.asStr}/submitted`,   params: { ts, cbm } }),
          summary:     ({ ts, claimId      }: { ts?: string, claimId: CLM.ClaimId               }) => url({ app: CLM.App.CLAIMS_DIS, path: `/claims/${claimId.asStr}`,             params: { ts      } }),
          approvals:   ({ ts, claimId      }: { ts?: string, claimId: CLM.ClaimId               }) => url({ app: CLM.App.CLAIMS_DIS, path: `/claims/${claimId.asStr}/approvals`,   params: { ts      } }),
          products:    ({ ts, claimId      }: { ts?: string, claimId: CLM.ClaimId               }) => url({ app: CLM.App.CLAIMS_DIS, path: `/claims/${claimId.asStr}/products`,    params: { ts      } }),
          destruction: ({ ts, claimId      }: { ts?: string, claimId: CLM.ClaimId               }) => url({ app: CLM.App.CLAIMS_DIS, path: `/claims/${claimId.asStr}/destruction`, params: { ts      } }),
          credit:      ({ ts, claimId      }: { ts?: string, claimId: CLM.ClaimId               }) => url({ app: CLM.App.CLAIMS_DIS, path: `/claims/${claimId.asStr}/credit`,      params: { ts      } }),
          files:       ({ ts, claimId      }: { ts?: string, claimId: CLM.ClaimId               }) => url({ app: CLM.App.CLAIMS_DIS, path: `/claims/${claimId.asStr}/files`,       params: { ts      } }),
          draftClaim: {
            step1:  ({ ts, claimId }                      : { ts?: string, claimId: CLM.ClaimId })                                 => url({ app: CLM.App.CLAIMS_DIS, path: `/claims/${claimId.asStr}/draft/step-1`, params: { ts                               } }),
            step2:  ({ ts, claimId, editProductSelection }: { ts?: string, claimId: CLM.ClaimId , editProductSelection?: boolean}) => url({ app: CLM.App.CLAIMS_DIS, path: `/claims/${claimId.asStr}/draft/step-2`, params: { ts: 'NONE', editProductSelection } }),
            step3:  ({ ts, claimId }                      : { ts?: string, claimId: CLM.ClaimId })                                 => url({ app: CLM.App.CLAIMS_DIS, path: `/claims/${claimId.asStr}/draft/step-3`, params: { ts                               } }),
            step4:  ({ ts, claimId }                      : { ts?: string, claimId: CLM.ClaimId })                                 => url({ app: CLM.App.CLAIMS_DIS, path: `/claims/${claimId.asStr}/draft/step-4`, params: { ts                               } }),
            step5:  ({ ts, claimId }                      : { ts?: string, claimId: CLM.ClaimId })                                 => url({ app: CLM.App.CLAIMS_DIS, path: `/claims/${claimId.asStr}/draft/step-5`, params: { ts                               } }),
            submit: ({ ts, claimId }                      : { ts?: string, claimId: CLM.ClaimId })                                 => url({ app: CLM.App.CLAIMS_DIS, path: `/claims/${claimId.asStr}/draft/submit`, params: { ts                               } }),
          }
        }
      }
    },
    tracker: {
      noAccess: ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.TRACKER, path: `/no-access`, params: { ts } }),
      home:     ({ ts }: { ts?: string } = {}) => coreUrls.tracker.claims({ ts }),
      claims:   ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.TRACKER, path: `/claims`,     params: { ts: "NONE" } }),
      newClaim: ({ ts }: { ts?: string } = {}) => url({ app: CLM.App.TRACKER, path: `/claims/new`, params: { ts } }),
      draftClaim: {
        step1:     ({ ts, claimId }: { ts?: string, claimId: CLM.ClaimId }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/draft/step-1`,     params: { ts } }),
        step2:     ({ ts, claimId }: { ts?: string, claimId: CLM.ClaimId }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/draft/step-2`,     params: { ts } }),
        step3:     ({ ts, claimId }: { ts?: string, claimId: CLM.ClaimId }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/draft/step-3`,     params: { ts } }),
        step4:     ({ ts, claimId }: { ts?: string, claimId: CLM.ClaimId }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/draft/step-4`,     params: { ts } }),
        stepFinal: ({ ts, claimId }: { ts?: string, claimId: CLM.ClaimId }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/draft/step-final`, params: { ts } }),
      },
      claim: {
        summary:                    ({ ts, claimId, cbm }: { ts?: string, claimId: CLM.ClaimId, cbm?: string }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}`,                                 params: { ts, cbm } }),
        products:                   ({ ts, claimId, cbm }: { ts?: string, claimId: CLM.ClaimId, cbm?: string }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/products`,                        params: { ts, cbm } }),
        claimApprovals:             ({ ts, claimId, cbm }: { ts?: string, claimId: CLM.ClaimId, cbm?: string }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/claim-approvals`,                 params: { ts, cbm } }),
        destructionIntent:          ({ ts, claimId, cbm }: { ts?: string, claimId: CLM.ClaimId, cbm?: string }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/destruction-intent`,              params: { ts, cbm } }),
        ttbSubmission:              ({ ts, claimId, cbm }: { ts?: string, claimId: CLM.ClaimId, cbm?: string }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/ttb-submission`,                  params: { ts, cbm } }),
        destructionApprovals:       ({ ts, claimId, cbm }: { ts?: string, claimId: CLM.ClaimId, cbm?: string }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/destruction-approvals`,           params: { ts, cbm } }),
        destruction:                ({ ts, claimId, cbm }: { ts?: string, claimId: CLM.ClaimId, cbm?: string }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/destruction`,                     params: { ts, cbm } }),
        accounting:                 ({ ts, claimId, cbm }: { ts?: string, claimId: CLM.ClaimId, cbm?: string }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/accounting`,                      params: { ts, cbm } }),
        files:                      ({ ts, claimId, cbm }: { ts?: string, claimId: CLM.ClaimId, cbm?: string }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/files`,                           params: { ts, cbm } }),
        issues:                     ({ ts, claimId, cbm }: { ts?: string, claimId: CLM.ClaimId, cbm?: string }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/issues`,                          params: { ts, cbm } }),
        history:                    ({ ts, claimId      }: { ts?: string, claimId: CLM.ClaimId               }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/history`,                         params: { ts      } }),
        firstLevelClaimApproval:    ({ ts, claimId      }: { ts?: string, claimId: CLM.ClaimId               }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/claim-approvals/first-level`,     params: { ts      } }),
        newUpperLevelClaimApproval: ({ ts, claimId      }: { ts?: string, claimId: CLM.ClaimId               }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/claim-approvals/upper-level/new`, params: { ts      } }),

        upperLevelClaimApproval: ({ ts, claimId, ulcaId, cbm }: { ts?: string, claimId: CLM.ClaimId, ulcaId: CLM.UpperLevelClaimApprovalId, cbm?: string }) => url({ app: CLM.App.TRACKER, path: `/claims/${claimId.asStr}/claim-approvals/upper-level/${ulcaId.asStr}`, params: { ts, cbm } }),
      }
    }
  });

  type ClaimActionUrlFactory = UrlFactory<{ ts?: string, claimId: CLM.ClaimId }>;

  const actionStepUrl = (actionSteps: { action: CLP.Action, url: ClaimActionUrlFactory }[]) => (action: CLP.Action) => {
    const step = actionSteps.find(_ => _.action === action);
    assert(step !== undefined, `Unexpected action: ${action.identifier}. Available actions: ${actionSteps.map(_ => _.action.identifier).join(', ')}`);
    return step.url;
  }

  const tracker_AUTO_Claim = ({ ts, claim }: { ts?: string, claim: CLM.ClaimSummary }) => {
    const claimId = claim.id;

    const actionSteps = [
      { action: CLP.Action.APPROVE_CLAIM,                     url: coreUrls.tracker.claim.summary         },
      { action: CLP.Action.CONFIRM_CLAIM_ACCEPTANCE,          url: coreUrls.tracker.claim.summary         },
      { action: CLP.Action.RECORD_SAP_ORDER_NUMBER,           url: coreUrls.tracker.claim.summary         },
      { action: CLP.Action.CONFIRM_SAP_ORDER_CREATED,         url: coreUrls.tracker.claim.summary         },
      { action: CLP.Action.ENTER_WHOLESALER_POINT_OF_CONTACT, url: coreUrls.tracker.draftClaim.step2      },
      { action: CLP.Action.ENTER_CLAIM_APPROVAL_DETAILS,      url: coreUrls.tracker.draftClaim.step3      },
      { action: CLP.Action.UPLOAD_CLAIM_FORM,                 url: coreUrls.tracker.draftClaim.step4      },
      { action: CLP.Action.REVIEW_AND_OPEN_CLAIM,             url: coreUrls.tracker.draftClaim.stepFinal  },
    ];

    if ([CLP.Stage.DRAFT, CLP.Stage.SUBMITTED].includes(claim.stage)) {
      assert(claim.assigned !== undefined, "Claim must have an assigned action in DRAFT stage");
      const url = actionStepUrl(actionSteps)(claim.assigned?.action);
      return url({ ts, claimId });
    }

    return coreUrls.tracker.claim.summary({ ts, claimId });
  }

  const distributors_AUTO_Claim = ({ ts, claim }: { ts?: string, claim: CLM.ClaimSummary }) => {
    const claimId = claim.id;

    const actionSteps = [
      { action: CLP.Action.SELECT_PRODUCTS,            url: coreUrls.claimsApp.distributors.claim.draftClaim.step2 },
      { action: CLP.Action.ENTER_PRODUCT_DETAILS,      url: coreUrls.claimsApp.distributors.claim.draftClaim.step3 },
      { action: CLP.Action.FINISH_PRODUCT_DETAILS,     url: coreUrls.claimsApp.distributors.claim.draftClaim.step3 },
      { action: CLP.Action.SPECIFY_DESTRUCTION_INTENT, url: coreUrls.claimsApp.distributors.claim.draftClaim.step4 },
      { action: CLP.Action.VERIFY_POINT_OF_CONTACT,    url: coreUrls.claimsApp.distributors.claim.draftClaim.step5 },
      { action: CLP.Action.CONFIRM_AND_SUBMIT,         url: coreUrls.claimsApp.distributors.claim.summary          },
    ];

    if (claim.stage === CLP.Stage.DRAFT) {
      assert(claim.assigned !== undefined, "Claim must have an assigned action in DRAFT stage");
      const url = actionStepUrl(actionSteps)(claim.assigned?.action);
      return url({ ts, claimId });
    }

    if (claim.stage === CLP.Stage.SUBMITTED) {
      return coreUrls.claimsApp.distributors.claim.submitted({ ts, claimId });
    }

    return coreUrls.claimsApp.distributors.claim.summary({ ts, claimId });
  }

  const approvers_AUTO_Claim = ({ ts, claim }: { ts?: string, claim: CLM.ClaimSummary }) => {
    const claimId = claim.id;

    const actionSteps = [
      { action: CLP.Action.APPROVE_CLAIM, url: coreUrls.claimsApp.approvers.claim.summary },
    ];

    if (claim.stage === CLP.Stage.SUBMITTED && actionSteps.some(_ => _.action === claim.assigned?.action)) {
      assert(claim.assigned !== undefined, "Claim must have an assigned action in SUBMITTED stage");
      const url = actionStepUrl(actionSteps)(claim.assigned?.action);
      return url({ ts, claimId });
    }

    return coreUrls.claimsApp.approvers.claim.summary({ ts, claimId });
  }

  return {
    ...coreUrls,
    claimsApp: { ...coreUrls.claimsApp,  distributors: { ...coreUrls.claimsApp.distributors, AUTO_Claim: distributors_AUTO_Claim },
                                         approvers:    { ...coreUrls.claimsApp.approvers,    AUTO_Claim: approvers_AUTO_Claim    } },
    tracker:   { ...coreUrls.tracker,                                                        AUTO_Claim: tracker_AUTO_Claim        },
  };
}

export function staticUrls(urls: UrlFactoryMap): Array<{
  name: string,
  url:  string,
}> {
  assert(urls !== undefined, 'URLs map is undefined');

  function isStaticUrl(url: UrlFactory<any>): boolean {
    try {
      url({});
      return true;
    } catch (error) {
      return false;
    }
  }
  function isUrlFactory(item: any): item is UrlFactory<any> {
    return typeof item === 'function';
  }
  function isUrlFactoryMap(item: any): item is UrlFactoryMap {
    return typeof item === 'object' && item !== null && !Array.isArray(item) && Object.values(item).every(value => isUrlFactory(value) || isUrlFactoryMap(value));
  }
  const urlFactoryMatch = <TResult>(item: any, { urlFactory, urlFactoryMap }: {
    urlFactory:    (_: UrlFactory<any>) => TResult,
    urlFactoryMap: (_: UrlFactoryMap  ) => TResult,
  }): TResult => {
    if (isUrlFactory   (item)) return urlFactory   (item);
    if (isUrlFactoryMap(item)) return urlFactoryMap(item);
    unexpectedCodePath(`Unexpected item type in URLs map: ${typeof item}`);
  }

  function staticUrls_({ urls, parentName }: {
    urls:        UrlFactoryMap,
    parentName?: string
  }): Array<{
    name: string,
    url:  string,
  }> {
    assert(urls !== undefined, `URLs map is undefined in section ${parentName}`);

    return Object.entries(urls)
      .flatMap(([key, item]) => {
        const name = parentName ? `${parentName} / ${key}` : key;

        return urlFactoryMatch(item, {
          urlFactory:    _ => isStaticUrl(_) ? [{ name, url: _({}) }] : [],
          urlFactoryMap: _ => staticUrls_({ urls: _, parentName: name })
        });
      });
  }
  return staticUrls_({ urls });
}
